import { datadogLogs } from '@datadog/browser-logs';
import {
    fetchPostApplication,
    fetchPostState,
    fetchPutState,
    fetchPutApplication,
    PutStateRequestBody,
    PutApplicationRequestBody,
    PostApplicationRequestBody,
    PostStateRequestBody
} from '@iwoca/lapi-client/edge';
import { API_AUTH_TOKEN } from '@iwoca/lapi-client/lendingAPI';
import { useMutation } from '@tanstack/react-query';

import { useGetContext } from './lendingAPIQueries';

export function usePostStateMutation() {
    const { mutateAsync, isError } = useMutation({
        mutationFn: async ({ body }: { body: PostStateRequestBody }) => {
            const stateResponse = await fetchPostState({
                headers: {
                    authorization: API_AUTH_TOKEN
                },
                body
            });

            return {
                stateKey: stateResponse.data?.state_key
            };
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error('Fetch POST /state error', {
                    requestBody: variables,
                    error: error.message
                });
            }
        }
    });

    return {
        mutatePostState: mutateAsync,
        isNewStateError: isError
    };
}

export function usePutStateMutation() {
    const { context } = useGetContext();
    const existingStateKey = context?.state_key as string;

    const { mutateAsync, isError } = useMutation({
        mutationFn: async ({ body }: { body: PutStateRequestBody }) => {
            await fetchPutState({
                stateKey: existingStateKey,
                body
            });
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error(
                    'Fetch PUT /state/{state_key}/ error',
                    {
                        requestBody: variables,
                        error: error.message
                    }
                );
            }
        }
    });

    return {
        mutatePutState: mutateAsync,
        isStateUpdateError: isError
    };
}

export function usePostApplicationMutation() {
    const { mutateAsync, isError } = useMutation({
        mutationFn: async ({
            body,
            stateKey
        }: {
            body: PostApplicationRequestBody;
            stateKey: string;
        }) => {
            await fetchPostApplication({
                stateKey,
                body
            });
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error(
                    'Fetch POST /application/{state_key}/ error',
                    {
                        requestBody: variables,
                        error: error.message
                    }
                );
            }
        }
    });

    return {
        mutatePostApplication: mutateAsync,
        isNewApplicationError: isError
    };
}

export function usePutApplicationMutation() {
    const { context } = useGetContext();
    const existingStateKey = context?.state_key as string;

    const { mutateAsync, isError } = useMutation({
        mutationFn: async ({ body }: { body: PutApplicationRequestBody }) => {
            await fetchPutApplication({
                stateKey: existingStateKey,
                body
            });
        },
        onError: (error, variables) => {
            if (error instanceof Error) {
                datadogLogs.logger.error(
                    'Fetch PUT /application/{state_key}/ error',
                    {
                        requestBody: variables,
                        error: error.message
                    }
                );
            }
        }
    });

    return {
        mutatePutApplication: mutateAsync,
        isApplicationUpdateError: isError
    };
}
